import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  api, ApiFailure, ageLabel, genderShort,
  VISIT_TYPE_LABEL, VISIT_STATUS_LABEL,
  type Doctor, type PatientMatch, type QueueRow, type VisitPreview, type Gender,
  type PossibleDuplicate, type PatientCategory,
} from '../api'
import { CategoryPicker, CategoryPill } from '../components/PatientCategory'

const BLANK_PATIENT = {
  // Gender starts empty and the field is required: a silent default is a
  // wrong answer on every record where nobody touched the control.
  fullName: '', mobile: '', altMobile: '', gender: '' as Gender | '',
  // Like gender: no default, so a patient is never silently filed under a line.
  category: '' as PatientCategory | '',
  ageYears: '', ageMonths: '', bloodGroup: '', addressLine: '', city: '',
  guardianName: '', referredBy: '', allergies: '', chronicConditions: '',
}

/**
 * What the mobile box keeps: digits only, ten at most, as on the phone
 * screens. A 91 or 0 in front of a full number is dropped rather than the end
 * cut off. The box has no maxLength, since the browser would cut a pasted
 * '+91 98765 43210' to ten characters before this saw it.
 */
const mobileDigits = (v: string) => v.replace(/\D/g, '').replace(/^(91|0)(?=\d{10}$)/, '').slice(0, 10)

export default function Reception() {
  const navigate = useNavigate()
  const searchRef = useRef<HTMLInputElement>(null)

  // The top bar's patient search lands here with ?q=, so the search starts filled in.
  const [query, setQuery] = useState(() => new URLSearchParams(window.location.search).get('q') ?? '')
  const [matches, setMatches] = useState<PatientMatch[]>([])
  const [searching, setSearching] = useState(false)
  const [searched, setSearched] = useState(false)

  const [selected, setSelected] = useState<PatientMatch | null>(null)
  const [doctors, setDoctors] = useState<Doctor[]>([])
  const [doctorId, setDoctorId] = useState('')
  const [preview, setPreview] = useState<VisitPreview | null>(null)
  const [complaint, setComplaint] = useState('')
  const [emergency, setEmergency] = useState(false)

  const [registering, setRegistering] = useState(false)
  const [form, setForm] = useState({ ...BLANK_PATIENT })

  const [queue, setQueue] = useState<QueueRow[]>([])
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error' | 'warn'; text: string } | null>(null)
  /* Records the new patient resembles. Shown until the desk moves on. */
  const [duplicates, setDuplicates] = useState<PossibleDuplicate[]>([])
  /** Registration failures live inside the form, next to the button. */
  const [registerError, setRegisterError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const loadQueue = useCallback(async () => {
    const res = await api.get<{ visits: QueueRow[] }>('/api/visits')
    setQueue(res.visits)
  }, [])

  useEffect(() => {
    void (async () => {
      const res = await api.get<{ doctors: Doctor[] }>('/api/doctors')
      setDoctors(res.doctors)
      if (res.doctors[0]) setDoctorId(res.doctors[0].id)
      await loadQueue()
    })()
    searchRef.current?.focus()
  }, [loadQueue])

  // Debounced search. Mobile number first, which is how reception actually works.
  useEffect(() => {
    if (query.trim().length < 2) {
      setMatches([])
      setSearched(false)
      return
    }
    setSearching(true)
    const t = setTimeout(() => {
      void (async () => {
        try {
          const res = await api.get<{ patients: PatientMatch[] }>(
            `/api/patients/search?q=${encodeURIComponent(query.trim())}`,
          )
          setMatches(res.patients)
          setSearched(true)
        } finally {
          setSearching(false)
        }
      })()
    }, 250)
    return () => clearTimeout(t)
  }, [query])

  // Refresh the preview whenever the patient or doctor changes.
  useEffect(() => {
    if (!selected || !doctorId) { setPreview(null); return }
    void (async () => {
      try {
        const res = await api.get<{ preview: VisitPreview }>(
          `/api/patients/${selected.id}/next-visit?doctorId=${doctorId}`,
        )
        setPreview(res.preview)
      } catch {
        setPreview(null)
      }
    })()
  }, [selected, doctorId])

  function reset() {
    setSelected(null)
    setPreview(null)
    setComplaint('')
    setEmergency(false)
    setQuery('')
    setMatches([])
    setSearched(false)
    setRegistering(false)
    setForm({ ...BLANK_PATIENT })
    setDuplicates([])
    searchRef.current?.focus()
  }

  async function register(e: FormEvent) {
    e.preventDefault()
    setRegisterError(null)
    if (!form.category) {
      setRegisterError('Choose a patient category: Diabetic or Non-Diabetic.')
      return
    }
    setBusy(true)
    setMsg(null)
    try {
      const res = await api.post<{
        patient: PatientMatch
        possibleDuplicates: PossibleDuplicate[]
      }>('/api/patients', {
        ...form,
        ageYears: form.ageYears === '' ? null : Number(form.ageYears),
        ageMonths: form.ageMonths === '' ? null : Number(form.ageMonths),
      })
      setSelected({ ...res.patient, visitCount: 0, lastVisitDate: null })
      setRegistering(false)
      setForm({ ...BLANK_PATIENT })
      /*
       * The record is made either way - two real people do share a name, and
       * families share a mobile. But the desk is told what it resembles while
       * the patient is still standing there, which is the only moment the two
       * can be merged cheaply.
       */
      const dupes = (res.possibleDuplicates ?? []).filter((d) => d.id !== res.patient.id)
      setDuplicates(dupes)
      setMsg(dupes.length > 0
        ? {
          kind: 'warn',
          text: `Registered ${res.patient.fullName} — ${res.patient.patientCode}. `
            + `This looks like ${dupes.length === 1 ? 'an existing patient' : `${dupes.length} existing patients`}.`,
        }
        : { kind: 'ok', text: `Registered ${res.patient.fullName} — ${res.patient.patientCode}` })
    } catch (err) {
      // Beside the button that was just pressed, not at the top of a page the
      // form has already scrolled off.
      setRegisterError(err instanceof ApiFailure ? err.message : 'Could not register.')
    } finally {
      setBusy(false)
    }
  }

  async function startVisit() {
    if (!selected || !doctorId) return
    setBusy(true)
    setMsg(null)
    try {
      const res = await api.post<{ visit: { id: string; tokenNo: number } }>('/api/visits', {
        patientId: selected.id,
        doctorId,
        chiefComplaint: complaint || null,
        ...(emergency ? { visitType: 'emergency' } : {}),
      })
      await loadQueue()
      navigate(`/visits/${res.visit.id}`)
    } catch (err) {
      setMsg({ kind: 'error', text: err instanceof ApiFailure ? err.message : 'Could not open the visit.' })
      setBusy(false)
    }
  }

  const waiting = queue.filter((v) => v.status === 'waiting').length

  return (
    <>
      <div className="page-head">
        <h2>Reception</h2>
        <p>Search by mobile number, name or patient code. Register only if there is no match.</p>
      </div>

      {msg && <div className={`msg ${msg.kind}`}>{msg.text}</div>}

      {duplicates.length > 0 && (
        <div className="panel dupe-panel">
          <h3>Is this the same person?</h3>
          <p className="panel-note" style={{ marginTop: 0 }}>
            These records already share this mobile number or name. If one of them is the
            patient at the desk, use that record and ask an administrator to remove the one
            just created - the history hangs off the record, not the name.
          </p>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Patient</th><th>Code</th><th>Mobile</th><th></th></tr></thead>
              <tbody>
                {duplicates.map((d) => (
                  <tr key={d.id}>
                    <td>{d.fullName}</td>
                    <td className="mono">{d.patientCode}</td>
                    <td>{d.mobile ?? '—'}</td>
                    <td className="nowrap">
                      <button className="link" onClick={() => {
                        setQuery(d.patientCode)
                        setDuplicates([])
                        setSelected(null)
                      }}>Look it up</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="actions">
            <button className="secondary" onClick={() => setDuplicates([])}>
              Different person, carry on
            </button>
          </div>
        </div>
      )}

      <div className="panel">
        <div className="field">
          <label htmlFor="q">Find a patient</label>
          <input
            id="q"
            ref={searchRef}
            className="search"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelected(null) }}
            placeholder="Mobile number, name, or CLN-2026-00417"
            autoComplete="off"
          />
        </div>

        {query.trim().length >= 2 && !selected && (
          <div style={{ marginTop: 14 }}>
            {searching && <div className="empty">Searching…</div>}
            {!searching && matches.length > 0 && (
              <>
                <p className="panel-note" style={{ marginTop: 0 }}>
                  {matches.length} match{matches.length === 1 ? '' : 'es'} — check the age and last
                  visit before choosing. Families often share one mobile number.
                </p>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr><th>Name</th><th>Code</th><th>Age/Sex</th><th>Category</th><th>Mobile</th><th>Visits</th><th>Last seen</th><th></th></tr>
                    </thead>
                    <tbody>
                      {matches.map((p) => (
                        <tr key={p.id}>
                          <td>
                            {p.fullName}
                            {p.allergies && <> <span className="pill danger">allergy</span></>}
                          </td>
                          <td className="mono">{p.patientCode}</td>
                          <td className="num">{ageLabel(p)} / {genderShort(p.gender)}</td>
                          <td><CategoryPill category={p.category} /></td>
                          <td className="num">{p.mobile ?? '—'}</td>
                          <td className="num">{p.visitCount}</td>
                          <td className="num">{p.lastVisitDate ?? '—'}</td>
                          <td><button className="link" onClick={() => setSelected(p)}>Select</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {!searching && searched && matches.length === 0 && (
              <div className="empty">
                No patient matches “{query}”.
                <div style={{ marginTop: 12 }}>
                  <button onClick={() => {
                    setRegistering(true)
                    // A 91 or 0 searched in front of the number is dropped so it fits the box.
                    const isNumber = /^\d+$/.test(query.trim())
                    setForm({ ...BLANK_PATIENT, mobile: isNumber ? mobileDigits(query.trim()) : '', fullName: isNumber ? '' : query.trim() })
                  }}>
                    Register a new patient
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {!registering && !selected && query.trim().length < 2 && (
          <div className="actions">
            <button className="secondary" onClick={() => setRegistering(true)}>Register a new patient</button>
          </div>
        )}
      </div>

      {registering && (
        <form className="panel" onSubmit={(e) => void register(e)}>
          <h3>New patient</h3>
          <p className="panel-note">Only name, gender and category are required; the age and mobile can stay empty.</p>
          <div className="grid two">
            <div className="field">
              <label htmlFor="pn">Full name</label>
              <input id="pn" value={form.fullName} autoFocus
                onChange={(e) => setForm({ ...form, fullName: e.target.value })} required />
            </div>
            <div className="field">
              <label htmlFor="pm">Mobile number</label>
              {/* Optional: many patients (elderly, children, walk-ins) have no
                  phone. One that is given is how this patient is found again
                  forever after, so the shape is checked here rather than only
                  at the server, where the rejection lands at the top of the page. */}
              <input id="pm" value={form.mobile} inputMode="numeric"
                pattern="[6-9][0-9]{9}"
                title="Ten digits starting 6, 7, 8 or 9, no spaces or country code."
                onChange={(e) => setForm({ ...form, mobile: mobileDigits(e.target.value) })} />
              <span className="hint">Optional. Ten digits; this is how the patient is found again.</span>
            </div>
          </div>
          <div className="field" style={{ marginTop: 14 }}>
            <label id="pcat-label">Patient category</label>
            <CategoryPicker id="pcat" value={form.category}
              onChange={(category) => setForm({ ...form, category })} />
            <span className="hint">Diabetic or Non-Diabetic. Pick one - there is no default.</span>
          </div>
          <div className="grid three" style={{ marginTop: 14 }}>
            <div className="field">
              <label htmlFor="pg">Gender</label>
              {/* No default. Pre-selecting "Female" meant a distracted clerk
                  registered every male patient as female without touching it. */}
              <select id="pg" value={form.gender} required
                onChange={(e) => setForm({ ...form, gender: e.target.value as Gender | '' })}>
                <option value="">Select…</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="py">Age (years)</label>
              <input id="py" type="number" min={0} max={130} value={form.ageYears}
                onChange={(e) => setForm({ ...form, ageYears: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="pmo">Months <span style={{ textTransform: 'none' }}>(infants)</span></label>
              <input id="pmo" type="number" min={0} max={11} value={form.ageMonths}
                onChange={(e) => setForm({ ...form, ageMonths: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="pb">Blood group</label>
              <input id="pb" value={form.bloodGroup}
                onChange={(e) => setForm({ ...form, bloodGroup: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="pc">City</label>
              <input id="pc" value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="pr">Referred by</label>
              <input id="pr" value={form.referredBy}
                onChange={(e) => setForm({ ...form, referredBy: e.target.value })} />
            </div>
          </div>
          <div className="grid two" style={{ marginTop: 14 }}>
            <div className="field">
              <label htmlFor="pa">Known allergies</label>
              <input id="pa" value={form.allergies} placeholder="Penicillin, sulpha…"
                onChange={(e) => setForm({ ...form, allergies: e.target.value })} />
              <span className="hint">Shown in red on every screen and on the prescription.</span>
            </div>
            <div className="field">
              <label htmlFor="pcc">Chronic conditions</label>
              <input id="pcc" value={form.chronicConditions} placeholder="Diabetes, hypertension…"
                onChange={(e) => setForm({ ...form, chronicConditions: e.target.value })} />
            </div>
          </div>
          {registerError && (
            <div className="msg error" role="alert" style={{ marginTop: 14, marginBottom: 0 }}>
              {registerError}
            </div>
          )}
          <div className="actions">
            <button type="submit" disabled={busy}>Register</button>
            <button type="button" className="secondary" onClick={() => setRegistering(false)}>Cancel</button>
          </div>
        </form>
      )}

      {selected && (
        <div className="panel">
          <h3>Start a visit</h3>
          <div className="patient-strip">
            <div>
              <strong>{selected.fullName}</strong>
              <span className="mono"> {selected.patientCode}</span>
            </div>
            <div className="mono">{ageLabel(selected)} / {genderShort(selected.gender)}{selected.mobile ? ` · ${selected.mobile}` : ''}</div>
            <div><CategoryPill category={selected.category} /></div>
            {selected.allergies && <div className="allergy">Allergies: {selected.allergies}</div>}
            {selected.chronicConditions && <div className="chronic">{selected.chronicConditions}</div>}
          </div>

          <div className="grid two" style={{ marginTop: 16 }}>
            <div className="field">
              <label htmlFor="doc">Doctor</label>
              <select id="doc" value={doctorId} onChange={(e) => setDoctorId(e.target.value)}>
                {doctors.map((d) => (
                  <option key={d.id} value={d.id}>{d.fullName} — {d.specialization ?? 'General'}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="cc">Chief complaint</label>
              <input id="cc" value={complaint} placeholder="Headache, 3 days"
                onChange={(e) => setComplaint(e.target.value)} />
            </div>
          </div>

          {preview && (
            <div className="preview">
              <div className="preview-line">{preview.summary}</div>
              <div className="preview-fee">
                <span className={`pill ${preview.visitType === 'follow_up' ? 'on' : 'off'}`}>
                  {VISIT_TYPE_LABEL[preview.visitType]}
                </span>
                <span className="fee">₹{preview.fee}</span>
                {preview.visitType === 'follow_up' && (
                  <span className="hint">follow-up rate, within {preview.followUpWindowDays} days</span>
                )}
              </div>
            </div>
          )}

          <div className="actions">
            <label className="check">
              <input type="checkbox" checked={emergency} onChange={(e) => setEmergency(e.target.checked)} />
              Emergency — move to the front of the queue
            </label>
          </div>

          <div className="actions">
            <button onClick={() => void startVisit()} disabled={busy || !doctorId}>
              Start visit &amp; take payment
            </button>
            <button className="secondary" onClick={reset}>Cancel</button>
          </div>
        </div>
      )}

      <div className="panel">
        <h3>Today&rsquo;s queue {waiting > 0 && <span className="pill warn">{waiting} waiting</span>}</h3>
        {queue.length === 0 ? (
          <div className="empty">No visits yet today.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Tok</th><th>Patient</th><th>Age/Sex</th><th>Category</th><th>Doctor</th><th>Visit</th><th>Payment</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {queue.map((v) => (
                  <tr key={v.id}>
                    <td className="num"><span className="token">#{v.tokenNo}</span></td>
                    <td>
                      {v.patientName}
                      {v.allergies && <> <span className="pill danger">allergy</span></>}
                      <br /><span className="mono subtle">{[v.patientCode, v.mobile].filter(Boolean).join(' · ')}</span>
                    </td>
                    <td className="num">{ageLabel(v)} / {genderShort(v.gender)}</td>
                    <td><CategoryPill category={v.category} /></td>
                    <td>{v.doctorName}</td>
                    <td>
                      <span className="mono">#{v.visitNo}</span>{' '}
                      <span className={`pill ${v.visitType === 'emergency' ? 'danger' : v.visitType === 'follow_up' ? 'on' : 'off'}`}>
                        {VISIT_TYPE_LABEL[v.visitType]}
                      </span>
                    </td>
                    <td className="num">
                      {v.paymentStatus
                        ? <span className={`pill ${v.paymentStatus === 'paid' ? 'on' : 'warn'}`}>₹{v.amountPaid}</span>
                        : <span className="pill warn">not taken</span>}
                    </td>
                    <td><span className={`pill ${v.status === 'completed' ? 'on' : 'off'}`}>{VISIT_STATUS_LABEL[v.status]}</span></td>
                    <td><button className="link" onClick={() => navigate(`/visits/${v.id}`)}>Open</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}
